import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

export default function ScheduleGridItem({
	itemKey,
	position = {},
	rowStart = null,
	rowEnd = null,
	color = 'dark-purple',
	accentColor = null,
	selected = false,
	onClick = null,
	name = '',
	nameTooltipText = '',
	time = '',
	timeTooltipText = '',
	headerAction = null,
	headerMenuOptions = [],
	temporary = false,
	floating = false,
	conflict = false,
	busy = false,
	busyContent = null,
	topContent = null,
	hoverPopoverContent = null,
	children = null,
	className = '',
	innerClassName = '',
	style = {},
	innerStyle = {},
	contentStyle = {},
	nameStyle = {},
	timeStyle = {},
	...rest
}) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState(null);
	const [isNameTooltipVisible, setIsNameTooltipVisible] = useState(false);
	const [nameTooltipPosition, setNameTooltipPosition] = useState(null);
	const [isHoverPopoverVisible, setIsHoverPopoverVisible] = useState(false);
	const [hoverPopoverPosition, setHoverPopoverPosition] = useState(null);
	const itemRef = useRef(null);
	const menuRef = useRef(null);
	const menuToggleRef = useRef(null);
	const nameTooltipAnchorRef = useRef(null);
	const colStart = position.col_start || position.colStart;
	const colEnd = position.col_end || position.colEnd;
	const resolvedNameTooltip = nameTooltipText || name;
	const resolvedTimeTooltip = timeTooltipText || time;
	const selectedClassName = selected ? `${accentColor ? '' : `${color}-border `}mb-elem-block-selected` : '';
	const temporaryClassName = temporary ? 'schedule-grid-item-temporary' : '';
	const floatingClassName = floating ? 'schedule-grid-item-floating' : '';
	const conflictClassName = conflict ? 'schedule-grid-item-conflict' : '';
	const busyClassName = busy ? 'schedule-grid-item-busy' : '';
	const hasHeaderMenu = headerMenuOptions.length > 0;
	const accentClassName = accentColor ? '' : `left-border-${color}-thick`;
	const resolvedInnerStyle = {
		...(accentColor ? { borderLeft:`5px solid ${accentColor}` } : {}),
		...(accentColor && selected ? { borderColor:accentColor, borderTopWidth:'2px', borderRightWidth:'2px', borderBottomWidth:'2px' } : {}),
		...innerStyle
	};

	useEffect(() => {
		if(!isMenuOpen) return undefined;

		const handleDocumentClick = (e) => {
			if(menuRef.current && !menuRef.current.contains(e.target)) setIsMenuOpen(false);
		};

		document.addEventListener('click', handleDocumentClick);

		return () => document.removeEventListener('click', handleDocumentClick);
	}, [isMenuOpen]);

	useEffect(() => {
		if(!isMenuOpen || !menuToggleRef.current) return undefined;

		const updateMenuPosition = () => {
			if(!menuToggleRef.current) return;
			const toggleRect = menuToggleRef.current.getBoundingClientRect();
			setMenuPosition({
				top: toggleRect.bottom + 6,
				left: toggleRect.right
			});
		};

		updateMenuPosition();
		window.addEventListener('scroll', updateMenuPosition, true);
		window.addEventListener('resize', updateMenuPosition);

		return () => {
			window.removeEventListener('scroll', updateMenuPosition, true);
			window.removeEventListener('resize', updateMenuPosition);
		};
	}, [isMenuOpen]);

	useEffect(() => {
		if(!isNameTooltipVisible || !nameTooltipAnchorRef.current || !resolvedNameTooltip) return undefined;

		const updateTooltipPosition = () => {
			if(!nameTooltipAnchorRef.current) return;
			const nameRect = nameTooltipAnchorRef.current.getBoundingClientRect();
			setNameTooltipPosition({
				top: nameRect.bottom + 6,
				left: nameRect.left + (nameRect.width / 2)
			});
		};

		updateTooltipPosition();
		window.addEventListener('scroll', updateTooltipPosition, true);
		window.addEventListener('resize', updateTooltipPosition);

		return () => {
			window.removeEventListener('scroll', updateTooltipPosition, true);
			window.removeEventListener('resize', updateTooltipPosition);
		};
	}, [isNameTooltipVisible, resolvedNameTooltip]);

	useEffect(() => {
		if(!isHoverPopoverVisible || !itemRef.current || !hoverPopoverContent) return undefined;

		const updatePopoverPosition = () => {
			if(!itemRef.current) return;
			const itemRect = itemRef.current.getBoundingClientRect();
			setHoverPopoverPosition({
				top: itemRect.bottom + 6,
				left: itemRect.left + 8
			});
		};

		updatePopoverPosition();
		window.addEventListener('scroll', updatePopoverPosition, true);
		window.addEventListener('resize', updatePopoverPosition);

		return () => {
			window.removeEventListener('scroll', updatePopoverPosition, true);
			window.removeEventListener('resize', updatePopoverPosition);
		};
	}, [hoverPopoverContent, isHoverPopoverVisible]);

	if(!rowStart || !rowEnd || !colStart || !colEnd) return null;

	return (
		<div
			className={`mb-elem-block parent-div ${temporaryClassName} ${floatingClassName} ${busyClassName} ${className}`.trim()}
			ref={itemRef}
			style={{ gridColumnStart:colStart, gridColumnEnd:colEnd, gridRowStart:rowStart, gridRowEnd:rowEnd, ...style }}
			onClick={onClick}
			onMouseEnter={() => {
				if(hoverPopoverContent) setIsHoverPopoverVisible(true);
			}}
			onMouseLeave={() => {
				if(hoverPopoverContent) setIsHoverPopoverVisible(false);
			}}
			{...rest}
		>
			<div className={`mb-elem-block-inner ${accentClassName} ${selectedClassName} ${floatingClassName} ${conflictClassName} ${innerClassName}`.trim()} style={resolvedInnerStyle}>
				{topContent}
				<div className='mb-elem-block-header'>
					{resolvedNameTooltip ? (
						<span
							className='schedule-grid-item-name-anchor'
							ref={nameTooltipAnchorRef}
							onMouseEnter={() => setIsNameTooltipVisible(true)}
							onMouseLeave={() => setIsNameTooltipVisible(false)}
							onFocus={() => setIsNameTooltipVisible(true)}
							onBlur={() => setIsNameTooltipVisible(false)}
						>
							<div className='mb-elem-block-name ellipsis' style={nameStyle}>{name}</div>
						</span>
					) : (
						<div className='mb-elem-block-name ellipsis' style={nameStyle}>{name}</div>
					)}
					{hasHeaderMenu ? (
						<div className='schedule-grid-item-menu-parent parent-div' ref={menuRef}>
							<span ref={menuToggleRef}>
								<FontAwesomeIcon
									className='mb-elem-remove-block small-text cursor-pointer schedule-grid-item-menu-toggle'
									icon={faTimes}
									onClick={(e) => {
										e.stopPropagation();
										setIsMenuOpen((currentValue) => !currentValue);
									}}
								/>
							</span>
							{isMenuOpen && menuPosition && ReactDOM.createPortal(
								<>
									<div
										className='schedule-grid-item-menu-overlay'
										onClick={(e) => {
											e.stopPropagation();
											setIsMenuOpen(false);
										}}
									/>
									<div
										className='dropdown-options-container schedule-grid-item-menu-container'
										ref={menuRef}
										style={{ position:'fixed', top:`${menuPosition.top}px`, left:`${menuPosition.left}px`, transform:'translateX(-100%)' }}
									>
										{headerMenuOptions.map((option, optionIndex) => (
											<div
												className={`dropdown-option ${option.className || ''}`.trim()}
												key={`${itemKey}-menu-option-${optionIndex}`}
												onClick={(e) => {
													e.stopPropagation();
													setIsMenuOpen(false);
													if(option.onClick) option.onClick(e);
												}}
											>
												{option.label}
											</div>
										))}
									</div>
								</>,
								document.body
							)}
						</div>
					) : headerAction}
				</div>
				{isNameTooltipVisible && nameTooltipPosition && resolvedNameTooltip && ReactDOM.createPortal(
					<div
						className='schedule-grid-item-name-tooltip'
						style={{
							position:'fixed',
							top:`${nameTooltipPosition.top}px`,
							left:`${nameTooltipPosition.left}px`,
							transform:'translateX(-50%)'
						}}
					>
						{resolvedNameTooltip}
					</div>,
					document.body
				)}
				{isHoverPopoverVisible && hoverPopoverPosition && hoverPopoverContent && ReactDOM.createPortal(
					<div
						className='schedule-grid-item-hover-popover'
						style={{
							position:'fixed',
							top:`${hoverPopoverPosition.top}px`,
							left:`${hoverPopoverPosition.left}px`
						}}
					>
						{hoverPopoverContent}
					</div>,
					document.body
				)}
				<div className='mb-elem-block-content' style={contentStyle}>
					{resolvedTimeTooltip ? (
						<div className='tooltip' data-tooltip-text={resolvedTimeTooltip}>
							<div className='mb-elem-block-time ellipsis' style={timeStyle}>{time}</div>
						</div>
					) : (
						<div className='mb-elem-block-time ellipsis' style={timeStyle}>{time}</div>
					)}
				</div>
				{children}
				{busy && (
					<div className='schedule-grid-item-busy-overlay'>
						{busyContent}
					</div>
				)}
			</div>
		</div>
	);
}
